// DISCIPLAY — Shopify Theme · Core JS
// Cart drawer toggle, mobile menu, intersection observer, header scroll,
// 18+ age gate. Cookie consent lives in cookie-consent.js.
// rev 2 — force CDN cache bust for the reveal grace-timeout fix

(function () {
  'use strict';

  // ── Affiliate referral capture ─────────────────────
  // Persists ?ref= into localStorage (90-day window) so the kit
  // configurator can attribute the sale. Shape read by kit-builder.js.
  (function captureRef() {
    try {
      var ref = new URL(window.location.href).searchParams.get('ref');
      if (!ref) return;
      var expiry = Date.now() + 90 * 24 * 60 * 60 * 1000;
      localStorage.setItem('disciplay_ref', JSON.stringify({ ref: ref, expiry: expiry }));
    } catch (e) { /* no-op */ }
  })();

  // ── Reveal-on-scroll observer ─────────────────────
  // Reveals are FADE-IN DELIGHT, not gating of content. If a card is below
  // the fold and the user doesn't scroll within the first second, IO never
  // fires and the card sits at opacity:0 — which on PLP looks identical to
  // a sold-out / missing product. Force-mark everything visible after a
  // short grace window so below-fold content always appears.
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); });
  }, { threshold: 0.1 });
  function observeReveals() {
    document.querySelectorAll('.reveal:not(.visible), .reveal-left:not(.visible)').forEach((el) => revealObserver.observe(el));
  }
  observeReveals();
  // After 1.2s, any still-hidden .reveal becomes visible — covers all below-
  // fold cards on tall PLPs / long PDPs without requiring scroll.
  setTimeout(() => {
    document.querySelectorAll('.reveal:not(.visible), .reveal-left:not(.visible)').forEach((el) => el.classList.add('visible'));
  }, 1200);
  document.addEventListener('shopify:section:load', observeReveals); // Theme editor

  // ── Header region dropdown: close on outside click / Escape ──
  document.addEventListener('click', (e) => {
    const region = document.querySelector('[data-header-region]');
    if (!region || !region.hasAttribute('open')) return;
    if (e.target.closest('[data-header-region]')) return;
    region.removeAttribute('open');
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const region = document.querySelector('[data-header-region]');
    if (region && region.hasAttribute('open')) region.removeAttribute('open');
  });

  // ── Sticky header scroll state ────────────────────
  // Transparent at the very top of the page; frosts in as soon as the user scrolls,
  // so hero text never passes behind a fully-transparent header.
  const header = document.querySelector('sticky-header');
  function updateHeader() {
    if (!header) return;
    header.setAttribute('data-scrolled', window.scrollY > 40 ? 'true' : 'false');
  }
  window.addEventListener('scroll', updateHeader, { passive: true });
  updateHeader();

  // ── Nav dropdown stay-open ────────────────────────
  // Open on dropdown mouseenter; only close when cursor leaves BOTH the header bar
  // and the drawer (with a small grace timer so you can move between them).
  const headerBar = document.querySelector('.header-bar');
  const navDropdowns = document.querySelectorAll('.header-nav-dropdown');
  if (headerBar && navDropdowns.length) {
    let openDrop = null;
    let closeTimer = null;
    const openDropdown = (dd) => {
      clearTimeout(closeTimer);
      if (openDrop && openDrop !== dd) openDrop.classList.remove('is-open');
      dd.classList.add('is-open');
      openDrop = dd;
    };
    const scheduleClose = () => {
      closeTimer = setTimeout(() => {
        if (openDrop) { openDrop.classList.remove('is-open'); openDrop = null; }
      }, 140);
    };
    const cancelClose = () => clearTimeout(closeTimer);
    navDropdowns.forEach((dd) => {
      dd.addEventListener('mouseenter', () => openDropdown(dd));
      const drawer = dd.querySelector('.header-nav-submenu');
      if (drawer) {
        drawer.addEventListener('mouseenter', cancelClose);
        drawer.addEventListener('mouseleave', scheduleClose);
      }
    });
    headerBar.addEventListener('mouseenter', cancelClose);
    headerBar.addEventListener('mouseleave', scheduleClose);
  }

  // ── Mobile menu toggle + sliding panel stack ──────
  function getMobileMenu() { return document.querySelector('[data-mobile-menu]'); }
  function resetMobileMenuPanels(menu) {
    if (!menu) return;
    menu.querySelectorAll('[data-mobile-menu-panel]').forEach(panel => {
      panel.classList.remove('is-active', 'is-behind');
      if (panel.dataset.mobileMenuPanel === 'root') {
        panel.classList.add('is-active');
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }
  /*
    iOS-safe scroll lock. Setting body { overflow: hidden } isn't enough on iOS
    Safari — touch scrolling still propagates to the document. Fixing the body
    position and offsetting it by the current scrollY is the standard fix; we
    restore the scroll position on unlock so the user lands back where they were.
  */
  let _menuScrollY = 0;
  function lockScroll() {
    _menuScrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${_menuScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  }
  function unlockScroll() {
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    window.scrollTo(0, _menuScrollY);
  }
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-mobile-menu-toggle]');
    if (!trigger) return;
    const menu = getMobileMenu();
    if (!menu) return;
    const wasHidden = menu.hasAttribute('hidden');
    if (wasHidden) {
      resetMobileMenuPanels(menu);
      menu.removeAttribute('hidden');
      document.body.setAttribute('data-menu-open', '');
      lockScroll();
    } else {
      menu.setAttribute('hidden', '');
      document.body.removeAttribute('data-menu-open');
      unlockScroll();
    }
  });

  // Drill into a child panel (parent item with sub-links)
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-mobile-menu-open]');
    if (!opener) return;
    const menu = getMobileMenu();
    if (!menu) return;
    const targetId = opener.getAttribute('data-mobile-menu-open');
    const target = menu.querySelector(`[data-mobile-menu-panel="${targetId}"]`);
    if (!target) return;
    const root = menu.querySelector('[data-mobile-menu-panel="root"]');
    if (root) root.classList.add('is-behind');
    target.removeAttribute('hidden');
    // Force a reflow so the transition runs from translateX(100%) → 0
    void target.offsetWidth;
    target.classList.add('is-active');
  });

  // Back to root panel
  document.addEventListener('click', (e) => {
    const back = e.target.closest('[data-mobile-menu-back]');
    if (!back) return;
    const menu = getMobileMenu();
    if (!menu) return;
    const panel = back.closest('[data-mobile-menu-panel]');
    const root = menu.querySelector('[data-mobile-menu-panel="root"]');
    if (panel) {
      panel.classList.remove('is-active');
      setTimeout(() => panel.setAttribute('hidden', ''), 280);
    }
    if (root) root.classList.remove('is-behind');
  });

  // ── Cart drawer ───────────────────────────────────
  // Always re-query — the drawer DOM is swapped by refreshCart() so cached refs go stale.
  function getDrawer() { return document.querySelector('[data-cart-drawer]'); }
  function openCart() {
    const d = getDrawer();
    if (d) { d.classList.add('is-open'); document.body.style.overflow = 'hidden'; }
  }
  function closeCart() {
    const d = getDrawer();
    if (d) { d.classList.remove('is-open'); document.body.style.overflow = ''; }
  }
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-drawer-toggle]')) {
      e.preventDefault();
      const d = getDrawer();
      if (d && d.classList.contains('is-open')) closeCart(); else openCart();
    }
    if (e.target.classList.contains('cart-drawer-backdrop') || e.target.closest('[data-cart-close]')) {
      closeCart();
    }
  });

  // ── Ajax cart helpers (Shopify Cart API + Sections API) ──
  window.DISCIPLAY = window.DISCIPLAY || {};
  // Section IDs for the Section Rendering API — must match actual section IDs, not type names.
  // 'cart-drawer' is rendered via {% section 'cart-drawer' %} in layout → ID = 'cart-drawer'.
  // 'cart-template' is the main section on the cart page template (templates/cart.json → key "main") → ID = 'main'.
  const CART_SECTION_IDS = 'cart-drawer,main';

  // ── Optimistic line insertion ─────────────────────
  // Drops a placeholder <li class="cart-line"> into the drawer the moment
  // the user clicks ADD, so the side panel feels instant. The real server-
  // rendered line replaces it during refreshCart()'s wholesale swap. Caller
  // passes whatever bits it has (title/image/price/variant); missing fields
  // degrade gracefully.
  function escapeHtmlAttr(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }
  function buildOptimisticLine(p) {
    const li = document.createElement('li');
    li.className = 'cart-line cart-line--drawer cart-line--optimistic';
    li.setAttribute('data-cart-line-optimistic', '1');
    const qty = p.quantity || 1;
    const img = p.image
      ? `<img class="cart-line__image" src="${escapeHtmlAttr(p.image)}" alt="" loading="eager">`
      : '';
    const variant = p.variant
      ? `<div class="cart-line__variant">${escapeHtmlAttr(p.variant)}</div>` : '';
    li.innerHTML =
      `<span class="cart-line__image-link">${img}</span>` +
      `<div class="cart-line__info">` +
        `<span class="cart-line__title">${escapeHtmlAttr(p.title || '')}</span>` +
        variant +
        `<div class="cart-line__qty-row">` +
          `<div class="cart-line__qty">` +
            `<span class="cart-line__qty-btn" aria-hidden="true">−</span>` +
            `<span class="cart-line__qty-val">${qty}</span>` +
            `<span class="cart-line__qty-btn" aria-hidden="true">+</span>` +
          `</div>` +
        `</div>` +
      `</div>` +
      `<div class="cart-line__price">` +
        `<span class="cart-line__price-final">${escapeHtmlAttr(p.price || '')}</span>` +
      `</div>`;
    return li;
  }
  function bumpCartCount(delta) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      const m = el.textContent.match(/\d+/);
      const n = Math.max(0, (m ? parseInt(m[0], 10) : 0) + delta);
      if (el.classList.contains('cart-drawer-count')) {
        el.textContent = `(${n})`;
      } else {
        el.textContent = n;
        el.style.display = n > 0 ? '' : 'none';
      }
    });
  }
  function optimisticAdd(payload) {
    const drawer = getDrawer();
    if (!drawer) return null;
    const body = drawer.querySelector('[data-cart-body]');
    if (!body) return null;
    let list = body.querySelector('.cart-drawer-list');
    if (!list) {
      // Cart was empty — drop the empty-state markup and start a fresh list.
      body.innerHTML = '<ul class="cart-drawer-list" role="list"></ul>';
      list = body.querySelector('.cart-drawer-list');
    }
    const line = buildOptimisticLine(payload);
    list.insertBefore(line, list.firstChild);
    bumpCartCount(payload.quantity || 1);
    return line;
  }
  function rollbackOptimistic(line, quantity) {
    if (line && line.parentNode) line.parentNode.removeChild(line);
    bumpCartCount(-(quantity || 1));
  }

  window.DISCIPLAY.cart = {
    async fetch() {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      return r.json();
    },
    async add(items) {
      const body = { items: Array.isArray(items) ? items : [items], sections: CART_SECTION_IDS };
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await r.json();
      if (!r.ok) {
        const err = new Error(data.description || data.message || 'Add to cart failed');
        err.status = data.status || r.status;
        throw err;
      }
      await refreshCart(data.sections);
      return data;
    },
    optimisticAdd,
    rollbackOptimistic,
    async changeByKey(key, quantity) {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: CART_SECTION_IDS })
      });
      const data = await r.json();
      if (!r.ok) {
        const err = new Error(data.description || data.message || 'Cart update failed');
        err.status = data.status || r.status;
        throw err;
      }
      await refreshCart(data.sections);
      return data;
    },
  };

  async function refreshCart(sectionsPayload) {
    let sections = sectionsPayload;
    if (!sections) {
      const r = await fetch(`/?sections=${CART_SECTION_IDS}`);
      sections = await r.json();
    }
    if (sections && sections['cart-drawer']) {
      const tmp = document.createElement('div');
      tmp.innerHTML = sections['cart-drawer'];
      const next = tmp.querySelector('[data-cart-drawer]');
      const current = document.querySelector('[data-cart-drawer]');
      if (next && current) {
        const wasOpen = current.classList.contains('is-open');
        current.replaceWith(next);
        if (wasOpen) next.classList.add('is-open');
      }
    }
    if (sections && sections['main']) {
      const tmp = document.createElement('div');
      tmp.innerHTML = sections['main'];
      const next = tmp.querySelector('[data-cart-page]');
      const current = document.querySelector('[data-cart-page]');
      if (next && current) current.replaceWith(next);
    }
    const cart = await window.DISCIPLAY.cart.fetch();
    document.dispatchEvent(new CustomEvent('cart:update', { detail: cart }));
  }

  document.addEventListener('cart:update', (e) => {
    const count = (e.detail && e.detail.item_count) || 0;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = el.classList.contains('cart-drawer-count') ? `(${count})` : count;
      if (!el.classList.contains('cart-drawer-count')) {
        el.style.display = count > 0 ? '' : 'none';
      }
    });
  });

  // ── Cart line qty +/-, remove (optimistic UI) ─────
  document.addEventListener('click', (e) => {
    const qtyBtn = e.target.closest('[data-cart-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      const line = qtyBtn.closest('[data-cart-line]');
      const key = qtyBtn.getAttribute('data-line-key');
      const step = parseInt(qtyBtn.getAttribute('data-step'), 10) || 0;
      const valEl = line && line.querySelector('[data-cart-qty]');
      const current = valEl ? parseInt(valEl.textContent, 10) : 1;
      // Minimum 1 — the REMOVE button is the only path to delete a line.
      // Going to 0 here would silently drop the line server-side and then
      // any subsequent "+" can't restore it (line key no longer exists).
      const next = Math.max(1, current + step);
      if (next === current) return;
      if (valEl) valEl.textContent = next;
      window.DISCIPLAY.cart.changeByKey(key, next);
      return;
    }
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const key = removeBtn.getAttribute('data-line-key');
      const line = removeBtn.closest('[data-cart-line]');
      // Optimistic: collapse the row immediately so it feels instant
      if (line) {
        line.style.transition = 'opacity .15s, max-height .2s, padding .2s, margin .2s';
        line.style.opacity = '0';
        line.style.maxHeight = '0';
        line.style.padding = '0';
        line.style.overflow = 'hidden';
      }
      window.DISCIPLAY.cart.changeByKey(key, 0);
    }
  });

  // ── Cart page qty/remove also works via the same handlers ─
  // (selectors are shared between drawer and page)

  // ── 18+ Age gate ──────────────────────────────────
  const AGE_KEY = 'ds_age_confirmed_v1';
  const ageGate = document.querySelector('[data-age-gate]');
  if (ageGate && !localStorage.getItem(AGE_KEY)) {
    ageGate.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    ageGate.addEventListener('click', (e) => {
      if (e.target.closest('[data-age-confirm]')) {
        localStorage.setItem(AGE_KEY, '1');
        ageGate.setAttribute('hidden', '');
        document.body.style.overflow = '';
      }
      if (e.target.closest('[data-age-deny]')) {
        location.href = 'https://www.google.com';
      }
    });
  }

  // ── Newsletter modal trigger (open from header) ───
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-newsletter-open]')) {
      const modal = document.querySelector('[data-newsletter-modal]');
      if (modal) modal.removeAttribute('hidden');
    }
    if (e.target.closest('[data-newsletter-close]') || e.target.matches('.modal-bg')) {
      const modal = document.querySelector('[data-newsletter-modal]');
      if (modal) modal.setAttribute('hidden', '');
    }
    // Claim flow: apply discount cookie via /discount/CODE, then navigate to login.
    const claim = e.target.closest('[data-newsletter-claim]');
    if (claim) {
      e.preventDefault();
      const code = claim.dataset.discountCode;
      const loginUrl = claim.getAttribute('href');
      claim.classList.add('is-loading');
      // Fetch /discount/CODE sets the cookie; we ignore the response body and navigate ourselves.
      // credentials:'same-origin' is required so the Set-Cookie header sticks.
      fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin', redirect: 'manual' })
        .catch(() => {})
        .finally(() => { window.location.href = loginUrl; });
    }
  });

  // ── Section editor refresh hook ───────────────────
  document.addEventListener('shopify:section:load', () => {
    updateHeader();
  });

  // ── Sticky-header custom element so we can use the selector ─
  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', class extends HTMLElement {});
  }

  // ── PDP: thumbnail click → swap hero image ────────
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-pdp-thumb]');
    if (!thumb) return;
    const src = thumb.getAttribute('data-img-src');
    const hero = document.getElementById('pdp-hero-img');
    if (hero && src) {
      hero.style.opacity = '0';
      const next = new Image();
      next.onload = () => {
        // image_tag emits srcset; once we swap, the browser would otherwise
        // keep picking from the original srcset list. Drop it so src wins.
        hero.removeAttribute('srcset');
        hero.removeAttribute('sizes');
        hero.src = src;
        hero.style.opacity = '1';
      };
      next.src = src;
    }
    document.querySelectorAll('[data-pdp-thumb]').forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  });

  // ── PDP: qty stepper ──────────────────────────────
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const step = parseInt(btn.getAttribute('data-qty-step'), 10) || 0;
    const input = btn.parentElement.querySelector('input[name="quantity"]');
    if (!input) return;
    const next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // ── PDP: variant option radios → update hidden select + price + button state ─
  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-pdp-option]')) return;
    const form = e.target.closest('form');
    if (!form) return;
    const selected = Array.from(form.querySelectorAll('[data-pdp-option]:checked')).map((r) => r.value);
    const select = form.querySelector('#pdp-variant-id');
    if (!select) return;
    let matchedOpt = null;
    // Find matching variant option by comparing all option values in order
    Array.from(select.options).forEach((opt) => {
      // Variant title in Shopify is "Option1 / Option2 / Option3"
      const variantValues = opt.textContent.split(' / ').map((v) => v.trim());
      if (selected.every((v, i) => v === variantValues[i])) {
        select.value = opt.value;
        matchedOpt = opt;
      }
    });
    // Update add-to-cart button state based on selected variant availability
    const addBtn = form.querySelector('[name="add"]');
    const addText = addBtn && addBtn.querySelector('.pdp__add-text');
    if (addBtn && matchedOpt) {
      const available = matchedOpt.getAttribute('data-available') === 'true';
      addBtn.disabled = !available;
      if (addText) addText.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
    }
    // Toggle active class on labels for visual state
    form.querySelectorAll('.pdp__option-value').forEach((label) => {
      const radio = label.querySelector('[data-pdp-option]');
      if (radio) label.classList.toggle('is-active', radio.checked);
    });
  });

  // Scrape what we know from the PDP DOM so the optimistic line looks real.
  function scrapePdpLinePayload(form, quantity) {
    const root = form.closest('.pdp__info') || document;
    const title = (document.querySelector('.pdp__title') || {}).textContent || '';
    const hero = document.getElementById('pdp-hero-img');
    const image = hero ? (hero.currentSrc || hero.src) : '';
    const select = form.querySelector('#pdp-variant-id');
    const opt = select && select.options[select.selectedIndex];
    let variant = '';
    if (opt && opt.textContent && opt.textContent.trim() !== 'Default Title') {
      variant = opt.textContent.trim();
    }
    // Prefer the option's data-price (always a money string); fall back to
    // the rendered price element.
    let price = (opt && opt.dataset && opt.dataset.price) || '';
    if (!price) {
      const priceEl = root.querySelector('.price__sale, .price__regular');
      if (priceEl) price = priceEl.textContent.trim();
    }
    return { title: title.trim(), variant, image, price, quantity };
  }

  // ── PDP: form submit via AJAX (optimistic — insert line + open drawer
  // before /cart/add.js fires; refresh swaps the real line on response).
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('.pdp__form');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[name="add"]');
    const txt = btn && btn.querySelector('.pdp__add-text');
    const variantId = form.querySelector('[name="id"]') && parseInt(form.querySelector('[name="id"]').value, 10);
    const quantity = parseInt(form.querySelector('[name="quantity"]') ? form.querySelector('[name="quantity"]').value : 1, 10) || 1;
    if (!variantId) return;
    if (btn) btn.disabled = true;
    if (txt) txt.textContent = 'ADDING…';
    openCart();
    const payload = scrapePdpLinePayload(form, quantity);
    const optimisticLine = optimisticAdd(payload);
    try {
      await window.DISCIPLAY.cart.add({ id: variantId, quantity });
      if (txt) txt.textContent = '✓ ADDED TO CART';
    } catch (err) {
      // /cart/add.js rejected — drop the optimistic line, restore count.
      // Throws without `.status` mean the add itself succeeded but a downstream
      // refresh call failed; in that case the swap already replaced the line,
      // so leave it alone.
      if (err && err.status) rollbackOptimistic(optimisticLine, quantity);
      if (txt) txt.textContent = 'TRY AGAIN';
    } finally {
      setTimeout(() => {
        if (btn) btn.disabled = false;
        if (txt) txt.textContent = 'ADD TO CART';
      }, 2000);
    }
  });

  // ── Search drawer (predictive products + articles + collections, tab UI) ─
  (function () {
    const drawer = document.querySelector('[data-search-drawer]');
    if (!drawer) return;
    const input = drawer.querySelector('[data-search-input]');
    const form = drawer.querySelector('[data-search-form]');
    const chipsBox = drawer.querySelector('[data-search-chips]');
    const tabs = Array.from(drawer.querySelectorAll('[data-search-tab]'));
    const panels = {
      products:    drawer.querySelector('[data-tab-panel="products"]'),
      collections: drawer.querySelector('[data-tab-panel="collections"]'),
      articles:    drawer.querySelector('[data-tab-panel="articles"]'),
    };
    const counts = {
      products:    drawer.querySelector('[data-tab-count="products"]'),
      collections: drawer.querySelector('[data-tab-count="collections"]'),
      articles:    drawer.querySelector('[data-tab-count="articles"]'),
    };
    let abortCtl = null;
    let debounceId = null;

    const drawerLocale = drawer.dataset || {};
    const noResultsTpl   = drawerLocale.tNoResults    || 'No results for "{{ terms }}".';
    const noResultsInTab = drawerLocale.tNoResultsTab || 'Nothing here for that one.';
    const viewAllLabel   = drawerLocale.tViewAll      || 'View all results';

    function open() {
      drawer.removeAttribute('hidden');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const mm = document.querySelector('[data-mobile-menu]');
      if (mm && !mm.hasAttribute('hidden')) mm.setAttribute('hidden', '');
      requestAnimationFrame(() => input && input.focus());
    }
    function close() {
      drawer.setAttribute('hidden', '');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (abortCtl) abortCtl.abort();
    }

    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-search-toggle]');
      if (!t) return;
      e.preventDefault();
      if (drawer.hasAttribute('hidden')) open(); else close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.hasAttribute('hidden')) close();
    });

    const moneyFormat = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    function formatMoney(cents) {
      if (typeof cents === 'string') return cents;
      const n = (parseInt(cents, 10) / 100).toFixed(2);
      return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, n);
    }
    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    }
    // Only allow <mark> in styled_text from Shopify Predictive Search.
    function safeStyled(html) {
      return String(html).replace(/<(?!\/?mark\b)[^>]*>/gi, '');
    }

    function setCount(key, n) {
      const el = counts[key];
      if (!el) return;
      if (n > 0) { el.textContent = '(' + n + ')'; el.removeAttribute('hidden'); }
      else { el.textContent = ''; el.setAttribute('hidden', ''); }
    }
    function emptyTab(term) {
      return `<p class="search-drawer-empty">${escapeHtml(noResultsInTab)}</p>`;
    }
    function viewAllLink(term, type) {
      const url = `/search?q=${encodeURIComponent(term)}&type=${type}`;
      return `<a class="search-drawer-view-all" href="${url}">${escapeHtml(viewAllLabel)} →</a>`;
    }

    function renderProducts(term, items) {
      if (!items.length) { panels.products.innerHTML = emptyTab(term); return; }
      const cards = items.map((p) => {
        const img = (p.featured_image && p.featured_image.url) || p.image || '';
        const price = p.price ? formatMoney(p.price) : '';
        return `<a href="${escapeHtml(p.url)}" class="search-drawer-product-card">
          <div class="search-drawer-product-card__media">
            ${img ? `<img src="${escapeHtml(img)}" alt="" loading="lazy">` : '<span class="search-drawer-product-card__placeholder"></span>'}
          </div>
          <div class="search-drawer-product-card__body">
            <h3 class="search-drawer-product-card__title">${escapeHtml(p.title)}</h3>
            ${price ? `<span class="search-drawer-product-card__price">${escapeHtml(price)}</span>` : ''}
          </div>
        </a>`;
      }).join('');
      panels.products.innerHTML = `<div class="search-drawer-products-grid">${cards}</div>${viewAllLink(term, 'product')}`;
    }

    function renderArticles(term, items) {
      if (!items.length) { panels.articles.innerHTML = emptyTab(term); return; }
      const rows = items.map((a) => {
        const img = (a.image && (a.image.url || a.image)) || (a.featured_image && a.featured_image.url) || '';
        const blogName = (a.blog && a.blog.title) || '';
        return `<a href="${escapeHtml(a.url)}" class="search-drawer-article-card">
          <div class="search-drawer-article-card__media">
            ${img ? `<img src="${escapeHtml(img)}" alt="" loading="lazy">` : '<span class="search-drawer-article-card__placeholder"></span>'}
          </div>
          <div class="search-drawer-article-card__body">
            ${blogName ? `<span class="search-drawer-article-card__kicker">${escapeHtml(blogName)}</span>` : ''}
            <h3 class="search-drawer-article-card__title">${escapeHtml(a.title)}</h3>
          </div>
        </a>`;
      }).join('');
      panels.articles.innerHTML = `<div class="search-drawer-articles-list">${rows}</div>${viewAllLink(term, 'article')}`;
    }

    function renderCollections(term, items) {
      if (!items.length) { panels.collections.innerHTML = emptyTab(term); return; }
      const chips = items.map((c) =>
        `<a href="${escapeHtml(c.url)}" class="search-drawer-collection-chip">${escapeHtml(c.title)}</a>`
      ).join('');
      panels.collections.innerHTML = `<div class="search-drawer-collections-list">${chips}</div>`;
    }

    function renderChips(queries) {
      if (!queries.length) { chipsBox.innerHTML = ''; chipsBox.setAttribute('hidden', ''); return; }
      chipsBox.removeAttribute('hidden');
      chipsBox.innerHTML = queries.slice(0, 6).map((q) => {
        const styled = q.styled_text ? safeStyled(q.styled_text) : escapeHtml(q.text || '');
        return `<button type="button" class="search-drawer-chip" data-chip-text="${escapeHtml(q.text || '')}">${styled}</button>`;
      }).join('');
    }

    function clearAll() {
      chipsBox.innerHTML = ''; chipsBox.setAttribute('hidden', '');
      Object.keys(panels).forEach((k) => { panels[k].innerHTML = ''; setCount(k, 0); });
    }

    function render(term, data) {
      const r = (data.resources && data.resources.results) || {};
      const products    = r.products || [];
      const articles    = r.articles || [];
      const collections = r.collections || [];
      const queries     = r.queries || [];

      renderChips(queries);
      renderProducts(term, products);
      renderArticles(term, articles);
      renderCollections(term, collections);
      setCount('products',    products.length);
      setCount('articles',    articles.length);
      setCount('collections', collections.length);
    }

    async function fetchSuggest(term) {
      if (abortCtl) abortCtl.abort();
      abortCtl = new AbortController();
      const url = '/search/suggest.json?q=' + encodeURIComponent(term)
        + '&resources[type]=product,article,collection,query'
        + '&resources[limit]=6'
        + '&resources[options][unavailable_products]=last';
      try {
        const res = await fetch(url, { signal: abortCtl.signal, headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('suggest_failed');
        const data = await res.json();
        render(term, data);
      } catch (err) {
        if (err.name !== 'AbortError') clearAll();
      }
    }

    function activateTab(key) {
      tabs.forEach((btn) => {
        const isMatch = btn.dataset.searchTab === key;
        btn.classList.toggle('is-active', isMatch);
        btn.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });
      Object.keys(panels).forEach((k) => {
        const isMatch = k === key;
        panels[k].classList.toggle('is-active', isMatch);
        if (isMatch) panels[k].removeAttribute('hidden'); else panels[k].setAttribute('hidden', '');
      });
    }

    tabs.forEach((btn) => {
      btn.addEventListener('click', () => activateTab(btn.dataset.searchTab));
    });

    if (chipsBox) {
      chipsBox.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-chip-text]');
        if (!chip || !input) return;
        input.value = chip.dataset.chipText;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      });
    }

    if (input) {
      input.addEventListener('input', () => {
        const term = input.value.trim();
        clearTimeout(debounceId);
        if (term.length < 2) { clearAll(); return; }
        debounceId = setTimeout(() => fetchSuggest(term), 200);
      });
    }
    if (form) {
      form.addEventListener('submit', (e) => {
        const term = input && input.value.trim();
        if (!term) { e.preventDefault(); input && input.focus(); }
      });
    }
  })();

  // ── PLP filter & sort drawer (Section Rendering API + history.pushState) ─
  (function () {
    function getSection() { return document.querySelector('[data-plp-section]'); }
    function getDrawer()  { return document.querySelector('[data-plp-drawer]'); }
    function getMain()    { const s = getSection(); return s && s.querySelector('[data-plp-main]'); }

    function openDrawer() {
      const d = getDrawer();
      if (!d) return;
      d.removeAttribute('hidden');
      requestAnimationFrame(() => d.classList.add('is-open'));
      document.body.style.overflow = 'hidden';
    }
    function closeDrawer() {
      const d = getDrawer();
      if (!d || d.hasAttribute('hidden')) return;
      d.classList.remove('is-open');
      // Match CSS transition duration before hiding (panel: .3s).
      setTimeout(() => {
        d.setAttribute('hidden', '');
        document.body.style.overflow = '';
      }, 300);
    }

    function clearFormFields() {
      const form = document.querySelector('[data-plp-form]');
      if (!form) return;
      form.querySelectorAll('input').forEach((inp) => {
        if (inp.type === 'checkbox' || inp.type === 'radio') inp.checked = false;
        else if (inp.type === 'number' || inp.type === 'text') inp.value = '';
      });
      // Restore the default sort selection so we never submit with no sort.
      const firstSort = form.querySelector('input[name="sort_by"][value="manual"]')
                    || form.querySelector('input[name="sort_by"]');
      if (firstSort) firstSort.checked = true;
    }

    function buildUrl(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        const s = String(value).trim();
        if (s !== '') params.append(key, s);
      });
      const action = form.getAttribute('action') || '/collections/all';
      const qs = params.toString();
      return qs ? `${action}?${qs}` : action;
    }

    async function swapFromUrl(url, scroll) {
      const section = getSection();
      if (!section) return;
      const sectionId = section.dataset.sectionId;
      const main = getMain();
      if (main) main.classList.add('is-loading');

      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', sectionId);

      try {
        const res = await fetch(u.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error('section_fetch_' + res.status);
        const html = await res.text();
        const wrap = document.createElement('div');
        wrap.innerHTML = html;

        // Swap toolbar (count + active filter badge), main grid, and drawer body.
        const pairs = [
          ['[data-plp-toolbar]', section.querySelector('[data-plp-toolbar]')],
          ['[data-plp-main]',    section.querySelector('[data-plp-main]')],
          ['[data-plp-drawer]',  section.querySelector('[data-plp-drawer]')]
        ];
        pairs.forEach(([selector, current]) => {
          const next = wrap.querySelector(selector);
          if (!next || !current) return;
          // Preserve drawer open state across the swap so re-applying filters
          // doesn't slam the drawer shut mid-action.
          if (selector === '[data-plp-drawer]') {
            const wasOpen = current.classList.contains('is-open');
            const wasHidden = current.hasAttribute('hidden');
            current.replaceWith(next);
            if (wasOpen) {
              next.removeAttribute('hidden');
              requestAnimationFrame(() => next.classList.add('is-open'));
              document.body.style.overflow = 'hidden';
            } else if (!wasHidden) {
              next.removeAttribute('hidden');
            }
          } else {
            current.replaceWith(next);
          }
        });

        // Newly-inserted .reveal elements (product cards) skipped the page-load
        // IntersectionObserver, so they're stuck at opacity:0. They're already
        // in the viewport — mark them visible immediately.
        section.querySelectorAll('.reveal, .reveal-left').forEach((el) => {
          el.classList.add('visible');
        });

        if (scroll) {
          const newMain = getMain();
          if (newMain) newMain.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (err) {
        console.error('[plp] swap failed', err);
        // Hard fallback: full page navigation so the user is never left stuck.
        location.href = url;
      } finally {
        const m = getMain();
        if (m) m.classList.remove('is-loading');
      }
    }

    function navigate(url) {
      history.pushState({ plp: true }, '', url);
      swapFromUrl(url, true);
    }

    // ── Event wiring (delegated to document so it survives section re-renders)
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-plp-open]'))   { e.preventDefault(); openDrawer(); return; }
      if (e.target.closest('[data-plp-close]'))  { closeDrawer(); return; }
      if (e.target.closest('[data-plp-clear-form]')) { e.preventDefault(); clearFormFields(); return; }
      const emptyClear = e.target.closest('[data-plp-clear]');
      if (emptyClear) {
        e.preventDefault();
        const form = document.querySelector('[data-plp-form]');
        const base = (form && form.getAttribute('action')) || '/collections/all';
        navigate(base);
        return;
      }
      const pageLink = e.target.closest('[data-plp-pagination] a');
      if (pageLink && pageLink.getAttribute('href')) {
        e.preventDefault();
        navigate(pageLink.getAttribute('href'));
      }
    });

    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-plp-form]');
      if (!form) return;
      e.preventDefault();
      const url = buildUrl(form);
      closeDrawer();
      navigate(url);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const d = getDrawer();
      if (d && !d.hasAttribute('hidden')) closeDrawer();
    });

    window.addEventListener('popstate', () => {
      if (!getSection()) return;
      swapFromUrl(location.href, false);
    });
  })();

  // ── Sticky-stack height sync ──────────────────────
  // Announcement bar + header both stick. Header's `top` offset must equal the
  // announcement bar's measured height so they don't overlap. Kept in a CSS
  // var (--ann-h) and refreshed on resize / content change. We also track
  // --header-h so the mobile menu drawer can sit directly below the header
  // (Maude-style — header stays visible while the drawer slides in).
  (function () {
    const ann = document.querySelector('.announce');
    const hdr = document.querySelector('sticky-header');
    function sync() {
      if (ann) document.documentElement.style.setProperty('--ann-h', ann.offsetHeight + 'px');
      if (hdr) document.documentElement.style.setProperty('--header-h', hdr.offsetHeight + 'px');
    }
    sync();
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(sync);
      if (ann) ro.observe(ann);
      if (hdr) ro.observe(hdr);
    }
    window.addEventListener('resize', sync, { passive: true });
  })();

  // ── Maude-style footer reveal ──────────────────────
  // Verified pattern (getmaude.com/cdn/shop/t/334/assets/custom-footer.css):
  //   - .site-footer is position:fixed at viewport bottom, opacity 0 by default
  //   - The section-group wrapper has height: var(--footer-h) so the document
  //     scroll-length still accounts for the footer's space
  //   - JS measures the footer's natural height (with position:fixed temporarily
  //     unset so the browser reports the real laid-out height) and sets --footer-h
  //   - On scroll, --footer-opacity is set to 1 when the reserved space at the
  //     end of the document has entered the viewport — i.e., we're close enough
  //     to the bottom that the user should see the footer fade in
  (function () {
    const footer = document.querySelector('.site-footer');
    if (!footer) return;

    let footerH = 0;
    let raf = 0;

    function measureFooter() {
      // Strip the fixed positioning briefly so offsetHeight reflects the actual
      // laid-out height. With position:fixed, the footer's height still works
      // for offsetHeight, but width is constrained — measuring this way is safer
      // when the footer has responsive content (e.g. mobile stacking).
      footerH = footer.offsetHeight;
      document.documentElement.style.setProperty('--footer-h', footerH + 'px');
    }

    function updateOpacity() {
      raf = 0;
      const docH = document.documentElement.scrollHeight;
      const vh = window.innerHeight;
      const scrollY = window.scrollY;
      // Distance from current viewport bottom to the bottom of the document
      const distFromBottom = docH - (scrollY + vh);
      // When the user is within footer-height pixels of the bottom, the reserved
      // footer space is starting to enter the viewport — fade in.
      // Smooth ramp: 1.0 at distFromBottom == 0, 0 at distFromBottom >= footerH
      let opacity;
      if (footerH === 0) opacity = 1;                            // safety
      else if (distFromBottom <= 0) opacity = 1;                 // at the bottom
      else if (distFromBottom >= footerH) opacity = 0;           // still well above
      else opacity = 1 - (distFromBottom / footerH);             // linear fade
      document.documentElement.style.setProperty('--footer-opacity', opacity.toFixed(3));
    }

    function schedule() {
      if (raf) return;
      raf = requestAnimationFrame(updateOpacity);
    }

    measureFooter();
    updateOpacity();

    if ('ResizeObserver' in window) {
      new ResizeObserver(() => { measureFooter(); schedule(); }).observe(footer);
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', () => { measureFooter(); schedule(); }, { passive: true });
  })();
})();
